<script setup>
import { onBeforeMount, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useLayout } from "@/layout/composables/layout";
import { AccessService } from "@/services/AccessService";

const route = useRoute();

const { layoutConfig, layoutState, setActiveMenuItem } = useLayout();

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  index: {
    type: Number,
    default: 0,
  },
  root: {
    type: Boolean,
    default: true,
  },
  parentItemKey: {
    type: String,
    default: null,
  },
});

const isActiveMenu = ref(false);
const itemKey = ref(null);
const subMenuRef = ref(null);
const menuItemRef = ref(null);

onBeforeMount(() => {
  itemKey.value = props.parentItemKey ? `${props.parentItemKey}-${props.index}` : String(props.index);

  const activeItem = layoutState.activeMenuItem;

  isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(`${itemKey.value}-`) : false;
  handleRouteChange(route.path);
});

watch(
  () => layoutState.activeMenuItem,
  (newVal) => {
    isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(`${itemKey.value}-`);
  },
);

watch(
  () => layoutConfig.menuMode,
  () => {
    isActiveMenu.value = false;
  },
);

watch(
  () => layoutState.overlaySubmenuActive,
  (newValue) => {
    if (!newValue) {
      isActiveMenu.value = false;
    }
  },
);
watch(
  () => route.path,
  (newPath) => {
    if (props.item.to && props.item.to === newPath) {
      setActiveMenuItem(itemKey);
    }
  },
);
watch(() => route.path, handleRouteChange);

function handleRouteChange(newPath) {
  if (props.item.to && props.item.to === newPath) {
    setActiveMenuItem(itemKey);
  }
}

async function itemClick(event, item) {
  if (item.disabled) {
    event.preventDefault();
    return;
  }

  if (item.command) {
    item.command({ originalEvent: event, item });
  }

  if (item.items) {
    setActiveMenuItem(isActiveMenu.value ? props.parentItemKey : itemKey);
  } else {
    setActiveMenuItem(itemKey);
  }
}

function checkActiveRoute(item) {
  return route.path === item.to;
}

function hasPermission(permission) {
  let resource = permission;
  let action = "read";
  if (permission.includes(":")) {
    [resource, action] = permission.split(":", 2);
  }
  const access = new AccessService(resource);
  return access.permits(action);
}

function visibleItems(items) {
  return items.filter((item) => {
    if (!item.permission) {
      return true;
    }
    if (Array.isArray(item.permission)) {
      return item.permission.some(hasPermission);
    }
    return hasPermission(item.permission);
  });
}
</script>

<template>
  <li
    ref="menuItemRef"
    :class="{
      'layout-root-menuitem': root,
      'active-menuitem': isActiveMenu,
      faded: item.wip,
    }"
  >
    <div v-if="root && item.visible !== false && visibleItems(item.items).length > 0" class="layout-menuitem-root-text">
      <span>{{ item.label }}</span> <i class="layout-menuitem-root-icon" />
    </div>
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      :class="item.class"
      :target="item.target"
      tabindex="0"
      @click="itemClick($event, item, index)"
    >
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon" />
      <i v-else-if="item.customIcon" class="layout-menuitem-icon">
        <Component :is="item.customIcon" class="layout-menuitem-icon h-auto" />
      </i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
    </a>
    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to"
      @click="itemClick($event, item, index)"
    >
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon" />
      <i v-else-if="item.customIcon" class="layout-menuitem-icon">
        <Component :is="item.customIcon" class="layout-menuitem-icon h-auto" />
      </i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
    </router-link>

    <ul v-if="item.items && item.visible !== false" ref="subMenuRef" :class="{ 'layout-root-submenulist': root }">
      <AppMenuItem
        v-for="(child, i) in visibleItems(item.items)"
        :key="child"
        :index="i"
        :item="child"
        :parent-item-key="itemKey"
        :root="false"
      />
    </ul>
  </li>
</template>

<style lang="scss" scoped>
.faded .layout-menuitem-text {
  color: var(--p-faded-color);
}
svg {
  &.layout-menuitem-icon {
    fill: currentColor;
    width: 1.28571429em;
    text-align: center;
  }
}
</style>
